import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getDeviceToken } from '../utils/deviceToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class ApiService {
    private client: AxiosInstance;

    constructor() {
        this.client = axios.create({
            baseURL: API_BASE_URL,
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor to add auth token and device token
        this.client.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                // Add device token if present (for IP bypass)
                // This function automatically restores from cookie if missing from localStorage
                const deviceToken = getDeviceToken();

                if (deviceToken) {
                    config.headers['X-Device-Token'] = deviceToken;
                    console.log(`🔐 [${config.method?.toUpperCase()}] ${config.url} - Device Token: ${deviceToken.substring(0, 15)}...`);
                } else {
                    console.warn(`⚠️ [${config.method?.toUpperCase()}] ${config.url} - NO DEVICE TOKEN FOUND`);
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor to handle auth errors
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    // Token expired or invalid
                    localStorage.removeItem('access_token');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    // Generic methods
    async get<T>(url: string, params?: any): Promise<T> {
        const response: AxiosResponse<T> = await this.client.get(url, { params });
        return response.data;
    }

    async post<T>(url: string, data?: any): Promise<T> {
        const response: AxiosResponse<T> = await this.client.post(url, data);
        return response.data;
    }

    async put<T>(url: string, data?: any): Promise<T> {
        const response: AxiosResponse<T> = await this.client.put(url, data);
        return response.data;
    }

    async patch<T>(url: string, data?: any): Promise<T> {
        const response: AxiosResponse<T> = await this.client.patch(url, data);
        return response.data;
    }

    async delete<T>(url: string): Promise<T> {
        const response: AxiosResponse<T> = await this.client.delete(url);
        return response.data;
    }

    // Form data for file uploads
    async postFormData<T>(url: string, formData: FormData): Promise<T> {
        const response: AxiosResponse<T> = await this.client.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }
}

export default new ApiService();